import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import appActions from '../../../appActions';
import PrimeTable from '../../../components/PrimeTable';
import SysAppLoggerFilterForm from './SysAppLoggerFilterForm';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';
import { Button } from 'primereact/button';
import { isPermissionCodeValid } from '../../../utils/mics';
import moment from 'moment';

const SysAppLoggerList  = ({ pageId }) => {
    
    const readAccess = isPermissionCodeValid(`${pageId}-R`);
    // const writeAccess = isPermissionCodeValid(`${pageId}-W`);
    const deleteAccess = isPermissionCodeValid(`${pageId}-D`)

    let navigate = useNavigate();
    const dispatch = useDispatch();

    const [deleteRecord, setDeleteRecord] = useState(null); // Store record for deletion
    
    const sysAppLoggerList = useSelector((state) => state.appLogger.sysAppLoggerList);

    const sysAppLogger = useSelector((state) => state.appLogger.sysAppLogger);

    const filters = useSelector((state) => state.appLogger.filters);
   

     const getDefaultParams = (filters = {}) => ({
        ...filters,
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder: -1,
        date_from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        date_till: moment().format('YYYY-MM-DD'),
    });

    const defaultParams = useMemo(() => getDefaultParams(filters), [filters]);
        
    const defaultParamsWithoutFilters = useMemo(() => getDefaultParams(), []);

    const [formValues, setFormValues] = useState(defaultParams);
    
    const handleFormValuesChange = (values) => {
        setFormValues(values);
    };

    const hasFetchedRef = useRef(false);

    useEffect(() => {
    if (!hasFetchedRef.current) {
        dispatch(appActions.SYSAPPLOGGER_GET_ALL_REQUEST(defaultParams));
        hasFetchedRef.current = true;
    }
    }, [dispatch, defaultParams]);

    const handleReset = () => {
        setFormValues(defaultParamsWithoutFilters)
        dispatch(appActions.SYSAPPLOGGER_GET_ALL_REQUEST(defaultParamsWithoutFilters));
    }

    const handleRefresh = useCallback(() => {
        dispatch(appActions.SYSAPPLOGGER_GET_ALL_REQUEST(defaultParamsWithoutFilters));
    }, [dispatch, defaultParamsWithoutFilters]);

    useEffect(() => {
        if (sysAppLogger?.action === 'delete') {
            handleRefresh();
        }
    }, [sysAppLogger, handleRefresh]);
    

    const handleSearch = useCallback(
        (params) => {
            const searchParams = {
                ...params,
                pagesize: params.pagesize || defaultParams?.pagesize,
                pageno: params.pageno || defaultParams?.pageno,
                sort: params.sort || defaultParams?.sort,
                sortOrder: params.sortOrder || defaultParams?.sortOrder,
            };
            dispatch(appActions.SYSAPPLOGGER_GET_ALL_REQUEST(searchParams));
        },
        [dispatch, defaultParams?.pageno, defaultParams?.pagesize, defaultParams?.sort, defaultParams?.sortOrder],  
    );

    const handleViewRedirect = useCallback((record) => {
        const data = sysAppLoggerList?.data?.find(t => t.id === record.id);
        navigate('/ui/system-app-logger/detail', { state: { data, isView: true } });
    }, [sysAppLoggerList?.data, navigate]);

    const handleDelete = useCallback((record) => {
        setDeleteRecord(record); 
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (deleteRecord) {
            dispatch(appActions.SYSAPPLOGGER_DELETE_REQUEST({ id: deleteRecord.id }));
            setDeleteRecord(null); // Clear the record after deletion
    
            // Explicitly refresh the table after deletion
            setTimeout(() => {
                handleRefresh();
            }, 500); // Add a slight delay if needed to allow the backend to process
        }
    }, [dispatch, deleteRecord, handleRefresh])

    const renderHeader = () => {
        return (
            <div className="d-flex flex-row gap-10 align-items-center">
                <h1 className="page-header flex">   System App Loggers </h1>
                <Button icon="pi pi-sync" rounded text aria-label="Refresh" className='mr-auto tp'  data-pr-tooltip="Refresh" onClick={handleRefresh} />
            </div>
        );
    };

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                {readAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                        <i className="pi pi-eye text-blue-500 text-base"></i>
                    </span>
                }
                {deleteAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                        <i className="pi pi-times-circle text-base text-danger" />
                    </span>
                }
            </div>
        ),
        [handleViewRedirect, handleDelete, readAccess, deleteAccess],  
    );

    const onFilterChangeCallback  = useCallback(
        (e) => {

            const { sortField, sortOrder, rows, page } = e;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));
            handleSearch(params); // Call search with updated sorting params
        },
        [handleSearch, formValues],
    );
 
    const columns = [
        { field: 'fileId', header: 'File Id', sortable: true, fileLog: true, width: '130px', isLink : true, linkUrl : "/ui/integration-file" },
        { field: 'fileName', header: 'File Name', sortable: true },
        { field: 'errorTag', header: 'Error Tag', sortable: true},
        { field: 'errorCode', header: 'Error Code', sortable: true, width: '130px'},
        { field: 'storeId', header: 'Store Id', sortable: true, width: '110px' },
        { field: 'alertGroup', header: 'Alert Group', sortable: true, width: '120px' },
        { field: 'alertPriority', header: 'Alert Priority', sortable: true, width: '130px' },
        { field: 'createdOn', header: 'Created On', sortable: true, width: '140px', columnType: 'datetime' },
        // { field: 'lastUpdated', header: 'Last Updated', sortable: true, columnType : 'datetime'  },
        // { field: 'lastUpdatedBy', header: 'Last Updated By', sortable: true },
    ];

    const renderList = () => {
        return (
            <>
                <div className='pv-15 ph-15'> {renderHeader()} </div>

                <div className="bg-white mx-3 pv-15 ph-15 br-6 shadow-white">
                    <SysAppLoggerFilterForm 
                        handleSearch={handleSearch} 
                        handleReset={handleReset}  
                        onFormValuesChange={handleFormValuesChange} 
                    />
                    <div className='custom-devider'></div>
                    <PrimeTable
                        list={sysAppLoggerList}
                        columns={columns}
                        actionColumn={actionTemplate}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        tableName='system_app_logger'
                        isLazy={true}
                        showGenerateXmlBtn={false}
                        pageId={ pageId }
                    />
                </div>
            </>
        );
    };

    return <div className="form_height">
            {renderList()}
            {deleteRecord && (
                <ConfirmationDialog
                    message={`Are you sure you want to delete the file : ${deleteRecord.fileName}?`}
                    onAccept={handleDeleteConfirm}
                    onReject={() => setDeleteRecord(null)}
                />
            )}
    </div>;
};


export default SysAppLoggerList;
